import React from 'react';
import { AllApplicationPermissions } from '../../Config/PermissionConfig';
import { isUserAuthorised } from '../../Hooks/usePermissionLoader';
import { Maybe, Nullable } from '../../Shared/Types';

type PermissionsCheckProps = {
    permission: AllApplicationPermissions | AllApplicationPermissions[];
    alternate?: Nullable<JSX.Element>;
};

export const PermissionsCheck: React.FC<PermissionsCheckProps> = props => {
    const isAuthorised = isUserAuthorised(props.permission);
    if (isAuthorised) return props.children as React.ReactElement;

    if (props.alternate) return props.alternate;

    return null;
};

type MassPermissionsCheckProps<T = {}> = {
    permissions: T;
    render(permissions: { [P in keyof T]?: boolean }): React.ReactNode;
};

export class MassPermissionsCheck<T = {}> extends React.Component<MassPermissionsCheckProps<T>> {
    public render() {
        const permissionResults: { [P in keyof T]?: boolean } = Object.entries(this.props.permissions).reduce(
            (
                currentPermissions,
                nextPermissions: [string, AllApplicationPermissions | AllApplicationPermissions[]]
            ) => {
                return {
                    ...currentPermissions,
                    [nextPermissions[0]]: isUserAuthorised(nextPermissions[1])
                };
            },
            {}
        );

        return this.props.render(permissionResults);
    }
}

export function withPermissionCheck<TProps = any>(
    WrappedComponent: any,
    permission?: AllApplicationPermissions | AllApplicationPermissions[],
    muiName?: string,
    alternate?: Nullable<JSX.Element>
) {
    return class extends React.Component<
        TProps & {
            permission?: AllApplicationPermissions | AllApplicationPermissions[];
            alternate?: Nullable<JSX.Element>;
        }
    > {
        public static muiName?: string = muiName;

        public render() {
            const permissionProp: Maybe<AllApplicationPermissions | AllApplicationPermissions[]> = this.props.permission
                ? this.props.permission
                : permission;
            const alternateProp = this.props.alternate ? this.props.alternate : alternate;

            if (WrappedComponent) {
                if (permissionProp)
                    return (
                        <PermissionsCheck permission={permissionProp} alternate={alternateProp}>
                            <WrappedComponent {...this.props} />
                        </PermissionsCheck>
                    );

                throw new Error('withPermissionCheck used with out any permission passed in');
            }
            return null;
        }
    };
}
